import {
  CSSProperties,
  DragEvent,
  FC,
  KeyboardEvent,
  useEffect,
  useRef,
  useState
} from 'react'

import { ContextMenu, ContextMenuTriggerOnActions } from '@inno-ui/context-menu'
import {
  DragHandleVerticalIcon,
  FolderArrowUpLineIcon,
  PencilLineIcon
} from '@inno-ui/icons'
import cn from 'classnames'
import { useShortcutGroupStore } from 'modules/desktop/pages/MainPage/stores'
import { getShortcutGroupCapabilities } from 'modules/desktop/shortcutGroups'
import { useTranslation } from 'react-i18next'
import {
  DesktopGridItem,
  ShortcutGroupBounds,
  ShortcutGroup as ShortcutGroupModel
} from 'shared/types/desktop-grid'

import { CellSize } from '../../types/grid.types'
import {
  getShortcutGroupBounds,
  getShortcutGroupTitleBarHeight
} from '../../utils/shortcutGroupBounds'
import styles from './ShortcutGroup.module.scss'

export type ShortcutGroupProps = {
  group: ShortcutGroupModel
  items: DesktopGridItem[]
  cellSize: CellSize
  isDropTarget?: boolean
  previewBounds?: ShortcutGroupBounds
  onDragStart: (groupId: string) => (event: DragEvent<HTMLElement>) => void
  onDragEnd: () => void
  onMoveByKeyboard: (
    groupId: string,
    delta: { row: number; col: number }
  ) => void
}

export const ShortcutGroup: FC<ShortcutGroupProps> = ({
  group,
  items,
  cellSize,
  isDropTarget = false,
  previewBounds,
  onDragStart,
  onDragEnd,
  onMoveByKeyboard
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'desktop.shortcut_group'
  })
  const renameGroup = useShortcutGroupStore(state => state.renameGroup)
  const deleteGroup = useShortcutGroupStore(state => state.deleteGroup)
  const bounds = getShortcutGroupBounds(group, items)
  const capabilities = getShortcutGroupCapabilities(group)
  const [isEditing, setIsEditing] = useState(false)
  const [draftTitle, setDraftTitle] = useState(group.title)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isEditing) return
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [isEditing])

  if (!bounds) return null

  const saveTitle = () => {
    if (!capabilities.canRename) return
    const nextTitle = draftTitle.trim().slice(0, 60)
    renameGroup(group.id, nextTitle || t('default_title'))
    setIsEditing(false)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') saveTitle()
    if (event.key === 'Escape') {
      setDraftTitle(group.title)
      setIsEditing(false)
    }
  }

  const visibleBounds = previewBounds ?? bounds
  const placement = {
    gridColumnStart: visibleBounds.position.col + 1,
    gridRowStart: visibleBounds.position.row + 1,
    gridColumnEnd: `span ${visibleBounds.size.width}`,
    gridRowEnd: `span ${visibleBounds.size.height}`
  }
  const width =
    cellSize.width * visibleBounds.size.width +
    cellSize.gapX * (visibleBounds.size.width - 1)
  const height =
    cellSize.height * visibleBounds.size.height +
    cellSize.gapY * (visibleBounds.size.height - 1)
  const topPadding = getShortcutGroupTitleBarHeight(cellSize)
  const options = [
    {
      icon: PencilLineIcon,
      label: t('rename'),
      onClick: () => {
        setDraftTitle(group.title)
        setIsEditing(true)
      }
    },
    { isSeparator: true },
    {
      icon: FolderArrowUpLineIcon,
      label: t('ungroup'),
      onClick: () => deleteGroup(group.id)
    }
  ]

  return (
    <section
      aria-label={group.title}
      data-shortcut-group-id={group.id}
      className={cn(styles.container, { [styles.isDropTarget]: isDropTarget })}
      style={
        {
          ...placement,
          width,
          height: height + topPadding,
          transform: `translateY(-${topPadding}px)`
        } as CSSProperties
      }
    >
      {isEditing && capabilities.canRename ? (
        <input
          ref={inputRef}
          className={styles.titleInput}
          aria-label={t('title_label')}
          value={draftTitle}
          onChange={event => setDraftTitle(event.target.value)}
          onBlur={saveTitle}
          onKeyDown={handleKeyDown}
        />
      ) : !capabilities.canRename ? (
        <span className={cn(styles.title, styles.lockedTitle)}>
          {group.title}
        </span>
      ) : (
        <ContextMenu
          triggerOnAction={ContextMenuTriggerOnActions.OnContextMenu}
          triggerNode={
            <button
              type='button'
              className={styles.title}
              aria-label={t('title_actions', { title: group.title })}
              onClick={() => {
                setDraftTitle(group.title)
                setIsEditing(true)
              }}
            >
              {group.title}
            </button>
          }
          options={options}
          stopEventPropagation
          zIndex={10}
        />
      )}
      <button
        type='button'
        className={styles.moveHandle}
        aria-label={t('move')}
        title={t('move')}
        draggable
        onMouseDown={event => event.stopPropagation()}
        onPointerDown={event => event.stopPropagation()}
        onDragStart={onDragStart(group.id)}
        onDragEnd={onDragEnd}
        onKeyDown={event => {
          const deltas: Record<string, { row: number; col: number }> = {
            ArrowUp: { row: -1, col: 0 },
            ArrowDown: { row: 1, col: 0 },
            ArrowLeft: { row: 0, col: -1 },
            ArrowRight: { row: 0, col: 1 }
          }
          const delta = deltas[event.key]
          if (!delta) return
          event.preventDefault()
          event.stopPropagation()
          onMoveByKeyboard(group.id, delta)
        }}
      >
        <DragHandleVerticalIcon color='var(--white)' />
      </button>
    </section>
  )
}
